import React, { useEffect, useState } from 'react';
import { useNavigate, useParams, useSearchParams } from 'react-router-dom';
import { useAuth } from '../../../contexts/AuthContext';
import { db } from '../../../firebase/config';
import {
  doc, getDoc, addDoc, updateDoc, collection, serverTimestamp,
} from 'firebase/firestore';
import {
  Box, Typography, Button, Paper, IconButton, TextField, CircularProgress,
} from '@mui/material';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import AddIcon from '@mui/icons-material/Add';
import DeleteIcon from '@mui/icons-material/Delete';
import DevicesIcon from '@mui/icons-material/Devices';
import PrintIcon from '@mui/icons-material/Print';
import PlayArrowIcon from '@mui/icons-material/PlayArrow';
import { GROUP_COLORS } from '../../workshop/utils/sessionCode';
import { SwotNLogo } from './SwotNAdmin';

const accent = '#E05500';

const fieldSx = {
  '& .MuiOutlinedInput-root': {
    '& fieldset': { borderColor: 'rgba(255,255,255,0.15)' },
    '&:hover fieldset': { borderColor: 'rgba(255,255,255,0.3)' },
    '&.Mui-focused fieldset': { borderColor: accent },
  },
  '& .MuiInputLabel-root.Mui-focused': { color: accent },
};

function newGroup(index) {
  return { id: crypto.randomUUID(), name: `Grupp ${index + 1}`, color: GROUP_COLORS[index % GROUP_COLORS.length] };
}

export default function SwotNDesign() {
  const { id }             = useParams();   // undefined when creating
  const navigate           = useNavigate();
  const [searchParams]     = useSearchParams();
  const { currentUser, userProfile } = useAuth();

  const isNew = !id;

  const [loading,  setLoading]  = useState(!isNew);
  const [saving,   setSaving]   = useState(false);

  const [title,   setTitle]   = useState('');
  const [question, setQuestion] = useState('');
  const [mode,    setMode]    = useState('digital');
  const [groups,  setGroups]  = useState([newGroup(0), newGroup(1), newGroup(2), newGroup(3)]);

  // Load existing session when editing
  useEffect(() => {
    if (isNew) {
      // Inherit groups from workshop if workshopId provided
      const workshopId = searchParams.get('workshopId');
      const preTitle   = searchParams.get('title') ? decodeURIComponent(searchParams.get('title')) : '';
      if (preTitle) setTitle(preTitle);
      if (workshopId) {
        getDoc(doc(db, 'workshops', workshopId)).then(snap => {
          if (snap.exists()) {
            const wGroups = snap.data()?.config?.groups || [];
            if (wGroups.length > 0) setGroups(wGroups);
          }
        }).catch(() => {});
      }
      return;
    }
    getDoc(doc(db, 'swot_sessions', id)).then(snap => {
      if (!snap.exists()) { navigate('/swotn'); return; }
      const d = snap.data();
      setTitle(d.title || '');
      setQuestion(d.question || '');
      setMode(d.mode || 'digital');
      setGroups(d.groups?.length ? d.groups : [newGroup(0), newGroup(1), newGroup(2), newGroup(3)]);
      setLoading(false);
    });
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [id]);

  const addGroup    = ()        => setGroups(g => [...g, newGroup(g.length)]);
  const removeGroup = (i)       => setGroups(g => g.filter((_, idx) => idx !== i));
  const updateGroup = (i, f, v) => setGroups(g => g.map((x, idx) => idx === i ? { ...x, [f]: v } : x));

  const handleSave = async () => {
    if (!title.trim()) return;
    setSaving(true);
    try {
      if (isNew) {
        const workshopId = searchParams.get('workshopId') || null;
        const ref = await addDoc(collection(db, 'swot_sessions'), {
          title: title.trim(),
          question: question.trim(),
          groups: groups.filter(g => g.name.trim()),
          mode,
          createdBy: currentUser?.uid || '',
          createdByName: userProfile?.displayName || userProfile?.name || currentUser?.displayName || currentUser?.email || '',
          createdAt: serverTimestamp(),
          updatedAt: serverTimestamp(),
          templateId: 'classic',
          backgroundUrl: '',
          overlayOpacity: 60,
          panelBlur: 12,
          status: 'collecting',
          workshopId,
          cardCount: 0,
        });
        const dest = workshopId ? `/swotn/${ref.id}?workshopId=${workshopId}` : `/swotn/${ref.id}`;
        navigate(dest);
      } else {
        await updateDoc(doc(db, 'swot_sessions', id), {
          title: title.trim(),
          question: question.trim(),
          mode,
          groups: groups.filter(g => g.name.trim()),
          updatedAt: serverTimestamp(),
        });
        navigate(`/swotn/${id}`);
      }
    } finally {
      setSaving(false);
    }
  };

  if (loading) return (
    <Box sx={{ minHeight: '100vh', bgcolor: 'background.default', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
      <CircularProgress sx={{ color: accent }} />
    </Box>
  );

  return (
    <Box sx={{ minHeight: '100vh', bgcolor: 'background.default', color: 'text.primary', p: { xs: 2, md: 4 }, maxWidth: 800, mx: 'auto' }}>

      {/* Header */}
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 4 }}>
        <IconButton onClick={() => navigate(isNew ? '/swotn' : `/swotn/${id}`)} sx={{ color: 'text.secondary' }}>
          <ArrowBackIcon />
        </IconButton>
        <Box sx={{ flex: 1 }}>
          <SwotNLogo />
          <Typography color="text.secondary" fontSize="0.82rem" mt={0.3}>
            {isNew ? 'Ny SWOT-analys' : 'Redigera SWOT-analys'}
          </Typography>
        </Box>
        {!isNew && (
          <Button
            variant="outlined"
            startIcon={<PlayArrowIcon />}
            onClick={() => navigate(`/swotn/${id}`)}
            sx={{ color: '#4ade80', borderColor: '#4ade80', textTransform: 'none' }}
          >
            Öppna board
          </Button>
        )}
        <Button
          variant="contained"
          onClick={handleSave}
          disabled={saving || !title.trim()}
          sx={{ bgcolor: accent, '&:hover': { bgcolor: '#C94D00' }, textTransform: 'none', fontWeight: 600, minWidth: 120 }}
        >
          {saving
            ? <CircularProgress size={20} sx={{ color: '#fff' }} />
            : isNew ? 'Skapa SWOT' : 'Spara'}
        </Button>
      </Box>

      {/* Grundinformation */}
      <Paper sx={{ borderRadius: 2, p: 3, mb: 3 }}>
        <Typography fontWeight={600} mb={2}>Grundinformation</Typography>

        <TextField
          autoFocus fullWidth label="Titel *"
          value={title} onChange={e => setTitle(e.target.value)}
          onKeyDown={e => e.key === 'Enter' && handleSave()}
          sx={{ ...fieldSx, mb: 2 }}
          placeholder="t.ex. Teamets SWOT – BTM Göteborg Q2 2026"
        />
        <TextField
          fullWidth label="Frågeställning (valfritt)"
          value={question} onChange={e => setQuestion(e.target.value)}
          multiline rows={2}
          sx={{ ...fieldSx, mb: 2.5 }}
          placeholder="Vad vill ni analysera? Ju skarpare fråga desto bättre SWOT."
        />

        {/* Läge */}
        <Typography fontSize="0.78rem" color="text.secondary" mb={1}>Genomförandeläge</Typography>
        <Box sx={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 1.5 }}>
          {[
            { id: 'digital', Icon: DevicesIcon, label: 'Helt digitalt', desc: 'Deltagarna bidrar via länk eller QR. Facilitatorn lägger till kort direkt i boardet.' },
            { id: 'hybrid',  Icon: PrintIcon,   label: 'Hybrid',        desc: 'Skriv ut pappersblad, fyll i för hand, skanna med AI. Kombinerar fysiskt och digitalt.' },
          ].map(m => {
            const active = mode === m.id;
            return (
              <Box key={m.id} onClick={() => setMode(m.id)} sx={{
                p: 1.75, borderRadius: 2, cursor: 'pointer',
                border: `2px solid ${active ? accent : 'rgba(255,255,255,0.12)'}`,
                bgcolor: active ? accent + '0d' : 'transparent',
                transition: 'all 0.15s',
                '&:hover': { borderColor: accent + '88' },
              }}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 0.75 }}>
                  <m.Icon sx={{ fontSize: 20, color: active ? accent : 'text.secondary' }} />
                  <Typography fontWeight={700} fontSize="0.88rem" color={active ? accent : 'text.primary'}>
                    {m.label}
                  </Typography>
                </Box>
                <Typography fontSize="0.72rem" color="text.secondary" sx={{ lineHeight: 1.45 }}>
                  {m.desc}
                </Typography>
              </Box>
            );
          })}
        </Box>

        {mode === 'hybrid' && !isNew && (
          <Box sx={{ mt: 2, p: 2, bgcolor: '#E0550011', border: '1px solid #E0550033', borderRadius: 1.5 }}>
            <Typography fontSize="0.82rem" fontWeight={700} color={accent} mb={0.75}>Hybridläge</Typography>
            <Typography fontSize="0.72rem" color="text.secondary">
              Skriv ut hybridblad via boardets utskriftsfunktion för att samla in svar på papper.
            </Typography>
          </Box>
        )}
      </Paper>

      {/* Grupper */}
      <Paper sx={{ borderRadius: 2, p: 3, mb: 3 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', mb: 1.5 }}>
          <Typography fontWeight={600} sx={{ flex: 1 }}>Grupper</Typography>
          <Button size="small" startIcon={<AddIcon />} onClick={addGroup}
            sx={{ color: accent, textTransform: 'none' }}>
            Lägg till
          </Button>
        </Box>
        <Typography fontSize="0.72rem" color="text.secondary" mb={2} sx={{ lineHeight: 1.4 }}>
          Grupper används för hybridblad och för att särskilja bidrag i boardet.
          Ta bort alla grupper för att köra utan gruppindelning.
        </Typography>
        {groups.map((g, i) => (
          <Box key={g.id} sx={{ display: 'flex', alignItems: 'center', gap: 1.5, mb: 1.5 }}>
            <input
              type="color"
              value={g.color || accent}
              onChange={e => updateGroup(i, 'color', e.target.value)}
              style={{ width: 36, height: 36, border: 'none', background: 'none', cursor: 'pointer', borderRadius: 4 }}
            />
            <TextField
              size="small" fullWidth
              value={g.name} onChange={e => updateGroup(i, 'name', e.target.value)}
              placeholder={`Grupp ${i + 1}`}
              sx={fieldSx}
            />
            <IconButton onClick={() => removeGroup(i)}
              sx={{ color: 'text.disabled', '&:hover': { color: '#ef4444' } }}>
              <DeleteIcon fontSize="small" />
            </IconButton>
          </Box>
        ))}
        {groups.length === 0 && (
          <Typography fontSize="0.78rem" color="text.disabled" fontStyle="italic">
            Ingen gruppindelning — alla bidrar i ett gemensamt board.
          </Typography>
        )}
      </Paper>

    </Box>
  );
}
